import { UntitledIcon, type UntitledIconData } from '@faceup/icons'
import { ulArrowLeft } from '@faceup/icons/ulArrowLeft'
import { type RouteCallback, useNavigate } from '@faceup/router'
import { Button, Col, Row, Space, Tooltip, Typography } from '@faceup/ui-base'
import type { ReactNode } from 'react'
import { useIsTruncated } from '../hooks/useIsTruncated'
import { useContentHeaderActions } from './adminLayoutContext'
import { HintIcon } from './HintIcon'

type ContentHeaderProps = {
  title: ReactNode
  titleHint?: string
  description?: ReactNode
  centerContent?: ReactNode
  secondaryContent?: ReactNode
  arrowGoBackTo?: RouteCallback
  action?: {
    icon: UntitledIconData
    onAction: () => void
    label: string
    'data-test'?: string
  }
}

export const ContentHeader = ({
  title,
  titleHint,
  description,
  centerContent,
  secondaryContent,
  arrowGoBackTo,
  action,
}: ContentHeaderProps) => {
  const { ref: titleRef, isTruncated } = useIsTruncated<HTMLElement>()
  const navigate = useNavigate()
  const headerActions = useContentHeaderActions()

  return (
    <Space direction='vertical' size={20} style={{ width: '100%' }}>
      <div className='flex items-center justify-between'>
        <div className='flex min-h-11 min-w-0 items-center'>
          {arrowGoBackTo && (
            <Button
              type='text'
              onClick={() => navigate(arrowGoBackTo)}
              icon={<UntitledIcon icon={ulArrowLeft} size={24} rtlFlip />}
              label=''
              className='print:hidden'
            />
          )}
          <Tooltip title={isTruncated ? title : ''}>
            <Typography.Title ellipsis level={3} ref={titleRef}>
              {title}
            </Typography.Title>
          </Tooltip>
          {action && (
            <Button
              onClick={action.onAction}
              type='text'
              icon={<UntitledIcon icon={action.icon} size={16} />}
              label={action.label}
              data-test={action['data-test']}
            />
          )}
          {titleHint && <HintIcon title={titleHint} variant='heading' />}
        </div>
        {centerContent && <div>{centerContent}</div>}
        {(secondaryContent || headerActions) && (
          <div
            className='flex items-center justify-end gap-2'
            style={{
              flexGrow: centerContent ? undefined : 1,
            }}
          >
            {secondaryContent}
            {headerActions && (
              <div ref={headerActions.attach} className='flex items-center gap-2 empty:hidden' />
            )}
          </div>
        )}
      </div>
      {description && (
        <Row>
          <Col span={24}>{description}</Col>
        </Row>
      )}
    </Space>
  )
}
